// Combat in the valley (phase B joined): the default scene's fight. The test valley's course, hero, mount and camera as
// before, plus the joined fight (src/enemies/fight.ts) on a navmesh built from the course's own colliders: the
// "valley" encounter's one wave, run through the director, places a camp of each enemy kind along the valley's north
// side and the Hollow Warden at the ruins in its north-east corner, idle until they sense the hero (no hunt pings).
// The hero fights with the player's presses (buffered attacks, skills, the big move and the dodge, the held block, the
// weapon switch), asks the targeting (src/targets/) what to turn to, and stands again at the hub after a defeat.
// The camps sit at least 17 m from every leg of the A2 course and face away from it, so the course's bots and feel
// probes run as before. `?encounter=off` boots the valley without the fight's encounter (an empty fight).
import type { BodySpec } from "@rpg/collision";
import { buildNavWorld, navGeometry, type NavShape } from "@rpg/nav";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import { FIGHT_LAYERS, FIGHT_SOLVER_ITERATIONS, createFight, readFightContent, type Fight } from "../enemies/fight";
import { MOUNT_SPAWN, SPAWN, VALLEY_LAYERS, buildCourse, terrainHeight } from "./course";
import type { ValleyVariant } from "./scene";

/** The pen's extent for the navmesh (the course's walls stand at ±31 m). */
const HALF = 32;
/** The navmesh's build settings for the valley (the arena's: an agent of the common enemy size). */
export const VALLEY_NAV = { cellSize: 0.25, cellHeight: 0.1, tileSize: 32, agentRadius: 0.4, agentHeight: 1.6, agentClimb: 0.35, maxSlope: 40, bounds: [[-HALF, -2, -HALF], [HALF, 6, HALF]] as [[number, number, number], [number, number, number]] };

/** Nav shapes from a scene's colliders: the same data the physics is built from (heightfields, boxes, upright
 *  cylinders, triangle meshes; sensors and water skipped). */
export function navShapesOf(bodies: readonly BodySpec[]): NavShape[] {
  const out: NavShape[] = [];
  for (const b of bodies) {
    if (b.kind !== "fixed") continue;
    const p = b.position ?? { x: 0, y: 0, z: 0 };
    const q = b.rotation;
    for (const c of b.colliders) {
      if (c.sensor) continue;
      const s = c.shape;
      if (s.type === "heightfield") out.push({ kind: "heightfield", rows: s.rows, cols: s.cols, heights: s.heights, size: [s.size.x, s.size.y, s.size.z], position: [p.x, p.y, p.z] });
      else if (s.type === "cuboid") out.push({ kind: "box", center: [p.x, p.y, p.z], halfExtents: [s.halfExtents.x, s.halfExtents.y, s.halfExtents.z], ...(q ? { rotation: q } : {}) });
      else if (s.type === "cylinder") out.push({ kind: "cylinder", center: [p.x, p.y, p.z], radius: s.radius, halfHeight: s.halfHeight });
      else if (s.type === "trimesh") out.push({ kind: "mesh", positions: s.vertices, indices: s.indices, position: [p.x, p.y, p.z], ...(q ? { rotation: q } : {}) });
    }
  }
  return out;
}

/** The fight's parts for the sim mount. */
export interface ValleyFightMount {
  variant: ValleyVariant;
  /** The fight once the scene is set up. */
  fight(): Fight;
}

/** The default valley with the fight. */
export function valleyFight(o: { content: SpikeContent; runtime: Runtime; difficulty?: string; encounter?: boolean }): ValleyFightMount {
  const content = readFightContent(o.content);
  let fight: Fight | null = null;
  const variant: ValleyVariant = {
    name: "valley",
    layers: { ...VALLEY_LAYERS, ...FIGHT_LAYERS },
    course: (t) => buildCourse(t),
    height: terrainHeight,
    spawn: SPAWN,
    mountSpawn: MOUNT_SPAWN,
    solverIterations: FIGHT_SOLVER_ITERATIONS,
    extend({ rig, rng, actions }) {
      const nav = buildNavWorld(navGeometry(navShapesOf(rig.course.bodies)), VALLEY_NAV);
      const f = createFight({
        collision: rig.collision,
        actions,
        rng,
        nav,
        content,
        slowMo: (scale, s, ease) => o.runtime.clock.slowMo(scale, s, ease),
        hero: () => rig.hero(),
        heroCtl: () => rig.heroCtl,
        cameraYaw: () => rig.control().cameraYaw,
        // 0051: the hero stands again with nothing lost; the valley's waypoint is its hub
        respawnAt: () => SPAWN,
        terrainAt: (x, z) => rig.terrainAt(x, z),
        encounter: o.encounter === false ? null : "valley",
        director: true,
        difficulty: o.difficulty ?? "normal",
      });
      fight = f;
      return { modules: f.modules, build: (w) => f.build(w), hashed: f.hashed, heroDrive: () => f.heroDrive() };
    },
  };
  return {
    variant,
    fight: () => {
      if (!fight) throw new Error("valley fight: not set up yet");
      return fight;
    },
  };
}
